import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Slider, Typography } from '@mui/material';
import { debounce } from 'lodash';
import BasicButton from '@components/BasicButton';
import { useTrainerToolsContent } from '@features/TrainerTools/provider';
import { useTrainerTools } from '@features/TrainerTools';
import { useModalCTAContext } from '@providers/ModalCTAProvider/ModalCTAProvider';
import { useMytaverse } from '@providers/MytaverseProvider';
import { Screens } from '@features/TrainerTools/interfaces';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { useStyles } from './styles';

const Settings = () => {
  const classes = useStyles();

  const { t: translate } = useTranslation();
  const { sendMessageToUnreal } = useMytaverse();

  const {
    setActiveScreen,
    sizeLimits,
    heightLimits,
    setActiveObject,
    setActiveObjectReady,
    setTemporaryObject,
    handleExploration,
    isExploration,

    height,
    setHeight,
    size,
    setSize,
  } = useTrainerToolsContent();

  const { setIsObjectShareActive } = useTrainerTools();

  const [modalOpen, setModalOpen] = useState(false);

  const debouncedMessage = useMemo(
    () =>
      debounce((property, value) => {
        sendMessageToUnreal({
          action: WebsocketAction.Asset3DSetDimension,
          payload: {
            property,
            value,
          },
        });
      }, 500),
    [],
  );

  const handleStop = useCallback(
    (screen: Screens) => {
      if (isExploration) {
        handleExploration();
      }

      sendMessageToUnreal({
        action: WebsocketAction.Asset3DStop,
      });
      setActiveObject(null);
      setTemporaryObject(null);
      setActiveObjectReady(false);

      if (screen) {
        setActiveScreen(screen);
      }
    },
    [isExploration, handleExploration],
  );

  const handleSetMaxsize = useCallback(() => {
    sendMessageToUnreal({
      action: WebsocketAction.Asset3DMaxSize,
    });

    setHeight(heightLimits.max);
    setSize(sizeLimits.max);
  }, [sizeLimits, heightLimits]);

  const { setValuesForModal } = useModalCTAContext();

  useEffect(() => {
    setValuesForModal({
      open: modalOpen,
      title: translate(`trainerToolModal.stopConfirmation.title`),
      message: translate(`trainerToolModal.stopConfirmation.text`),
      confirmButtonName: translate(
        `trainerToolModal.stopConfirmation.confirmButton`,
      ),
      cancelButtonName: translate(
        `trainerToolModal.stopConfirmation.cancelButton`,
      ),
      altStyling: true,
      onConfirm: () => {
        setModalOpen(false);
        setIsObjectShareActive(false);

        handleStop(Screens.INIT);
      },
      onCancel: () => {
        setModalOpen(false);
      },
    });
  }, [modalOpen, setValuesForModal, handleStop]);

  return (
    <>
      <Box>
        <Box sx={{ ...classes.slider, mb: 0.5 }}>
          <Typography sx={classes.label}>
            {translate('trainerToolModal.settings.height')}
          </Typography>
          <Slider
            aria-label="Height"
            value={height}
            min={heightLimits.min}
            max={heightLimits.max}
            step={10}
            onChange={(_, value) => {
              setHeight(value as number);
              debouncedMessage('height', value as number);
            }}
          />
        </Box>

        <Box sx={classes.slider}>
          <Typography sx={classes.label}>
            {translate('trainerToolModal.settings.size')}
          </Typography>
          <Slider
            aria-label="Size"
            value={size}
            min={sizeLimits.min}
            max={sizeLimits.max}
            step={0.1}
            onChange={(_, value) => {
              setSize(value as number);
              debouncedMessage('size', value as number);
            }}
          />
        </Box>

        <Box sx={classes.actions}>
          <BasicButton onClick={handleSetMaxsize} white sx={classes.button}>
            {translate('trainerToolModal.settings.maxSize')}
          </BasicButton>
          <BasicButton
            onClick={() => setActiveScreen(Screens.COLLECTION)}
            white
            sx={classes.button}
          >
            {translate('trainerToolModal.settings.replace')}
          </BasicButton>
          <BasicButton
            onClick={handleExploration}
            white
            primary={isExploration}
            sx={classes.button}
          >
            {translate('trainerToolModal.settings.exploration')}
          </BasicButton>
          <BasicButton
            onClick={() => setModalOpen(true)}
            primary
            sx={classes.button}
          >
            {translate('trainerToolModal.settings.stop')}
          </BasicButton>
        </Box>
      </Box>
    </>
  );
};

export default Settings;
